import { useRef, useCallback } from 'react';
import { VideoElement, ProgressPayload } from '../../types/player';

const PROGRESS_SAVE_INTERVAL = 10;

export const useProgressTracking = (
  videoElement: VideoElement,
  currentTime: number,
  duration: number,
  lessonDocumentId?: string,
  onProgressSave?: (progress: ProgressPayload) => void,
) => {
  const lastSaveTimeRef = useRef<number>(0);

  // Save progress via callback
  const saveLessonProgress = useCallback(
    (progressData: ProgressPayload) => {
      if (!onProgressSave) {
        console.warn('Cannot save lesson progress: onProgressSave callback not provided');
        return;
      }

      if (!lessonDocumentId) {
        console.warn('Cannot save lesson progress: lessonDocumentId not available');
        return;
      }

      // Call the provided callback with the progress data
      onProgressSave({
        current_second: progressData.current_second,
        progress_status: progressData.progress_status,
      });
    },
    [onProgressSave, lessonDocumentId],
  );

  // Track progress during playback
  const trackProgress = useCallback(() => {
    if (!videoElement) return;

    const currentTimeInSeconds = Math.floor(videoElement.currentTime);
    const lastSaveTime = lastSaveTimeRef.current;

    // Save progress at regular intervals
    if (
      currentTimeInSeconds > 0 &&
      currentTimeInSeconds % PROGRESS_SAVE_INTERVAL === 0 &&
      currentTimeInSeconds !== lastSaveTime
    ) {
      lastSaveTimeRef.current = currentTimeInSeconds;

      const progressPercentage =
        duration > 0 ? Math.min(Math.floor((currentTimeInSeconds / duration) * 100), 100) : 0;

      const isCompleted = progressPercentage >= 90;

      saveLessonProgress({
        current_second: isCompleted ? Math.round(duration) : currentTimeInSeconds,
        progress_status: isCompleted ? 'completed' : 'in_progress',
      });
    }
  }, [videoElement, duration, saveLessonProgress]);

  // Track progress on seek operations
  const trackSeekProgress = useCallback(
    (newTime: number) => {
      if (!videoElement) return;

      // Calculate progress percentage based on the new time
      const progressPercentage =
        duration > 0 ? Math.min(Math.floor((newTime / duration) * 100), 100) : 0;

      const isCompleted = progressPercentage >= 90;

      // Immediately trigger a lesson progress update when seeking
      saveLessonProgress({
        current_second: isCompleted ? Math.round(duration) : Math.round(newTime),
        progress_status: isCompleted ? 'completed' : 'in_progress',
      });
    },
    [videoElement, duration, saveLessonProgress],
  );

  // Handle toggle completion
  const handleToggleCompletion = useCallback(() => {
    if (!videoElement) return;

    const progressPercentage =
      duration > 0 ? Math.min(Math.floor((currentTime / duration) * 100), 100) : 0;

    const isCurrentlyCompleted = progressPercentage >= 90;

    // Toggle between completed and in progress
    saveLessonProgress({
      current_second: isCurrentlyCompleted ? Math.round(currentTime) : Math.round(duration),
      progress_status: isCurrentlyCompleted ? 'in_progress' : 'completed',
    });
  }, [videoElement, currentTime, duration, saveLessonProgress]);

  return {
    saveLessonProgress,
    trackProgress,
    trackSeekProgress,
    handleToggleCompletion,
  };
};
